import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  CheckSquare,
  FileText,
  Bell,
  Activity,
  Settings,
  UsersRound,
  PanelLeftClose,
  PanelLeft,
  Receipt,
  Repeat,
  HelpCircle,
  ChevronDown,
  Building,
} from "lucide-react";
import { useAuth, atLeast, hasRole } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useState } from "react";

const NAV = [
  {
    to: "/dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    testid: "nav-dashboard",
  },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, testid: "nav-tasks" },
  {
    to: "/tasks/recurring",
    label: "Recurring",
    icon: Repeat,
    testid: "nav-recurring",
  },
  { to: "/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices" },
  {
    to: "/documents",
    label: "Documents",
    icon: FileText,
    testid: "nav-documents",
  },
  {
    to: "/notifications",
    label: "Notifications",
    icon: Bell,
    testid: "nav-notifications",
  },
  {
    to: "/activity",
    label: "Activity",
    icon: Activity,
    testid: "nav-activity",
  },
];

function SystemNavSection({ collapsed }) {
  const location = useLocation();
  const isActive = location.pathname.startsWith("/system");
  const [open, setOpen] = useState(isActive);

  if (collapsed) {
    return (
      <div className="pt-4 mt-4 border-t border-border">
        <NavLink
          to="/system"
          className={cn(
            "flex items-center justify-center w-full py-2 rounded-md text-sm font-medium transition-colors hover:bg-muted/60",
            isActive ? "bg-secondary text-foreground" : "text-muted-foreground"
          )}
        >
          <Settings size={16} />
        </NavLink>
      </div>
    );
  }

  return (
    <div className="pt-4 mt-4 border-t border-border">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-muted/60",
          isActive && !open ? "text-foreground" : "text-muted-foreground"
        )}
      >
        <div className="flex items-center gap-3">
          <Settings size={16} />
          <span>System</span>
        </div>
        <ChevronDown size={14} className={cn("transition-transform", open ? "" : "-rotate-90")} />
      </button>
      
      {open && (
        <div className="mt-1 ml-4 space-y-0.5 border-l border-border pl-2">
          <NavLink
            to="/system/organizations"
            className={({ isActive: childActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                childActive
                  ? "bg-secondary text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
              )
            }
          >
            <Building size={14} />
            <span>Organizations</span>
          </NavLink>
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ collapsed, onToggle, unread }) {
  const { user } = useAuth();
  return (
    <aside
      data-testid="app-sidebar"
      className={cn(
        "border-r border-border bg-background flex flex-col h-screen sticky top-0 transition-[width] duration-200",
        collapsed ? "w-[64px]" : "w-[240px]",
      )}
    >
      <div className="h-14 flex items-center px-4 border-b border-border">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-7 h-7 rounded-md bg-foreground text-background flex items-center justify-center font-heading font-bold text-sm shrink-0">
            C
          </div>
          {!collapsed && (
            <div className="font-heading font-semibold tracking-tight truncate">
              CA OS
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={onToggle}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
        </Button>
      </div>

      <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={n.testid}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                isActive
                  ? "bg-secondary text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
              )
            }
          >
            <n.icon size={16} className="shrink-0" />
            {!collapsed && <span className="truncate">{n.label}</span>}
            {!collapsed && n.to === "/notifications" && unread > 0 && (
              <span className="ml-auto text-[10px] font-mono bg-foreground text-background rounded px-1.5 py-0.5">
                {unread}
              </span>
            )}
          </NavLink>
        ))}

        {atLeast(user, "partner") && (
          <NavLink
            to="/users"
            data-testid="nav-users"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                isActive
                  ? "bg-secondary text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
              )
            }
          >
            <UsersRound size={16} />
            {!collapsed && <span>Team</span>}
          </NavLink>
        )}

        <NavLink
          to="/settings"
          data-testid="nav-settings"
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
              isActive
                ? "bg-secondary text-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
            )
          }
        >
          <Settings size={16} />
          {!collapsed && <span>Settings</span>}
        </NavLink>

        <NavLink
          to="/help"
          data-testid="nav-help"
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
              isActive
                ? "bg-secondary text-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
            )
          }
        >
          <HelpCircle size={16} />
          {!collapsed && <span>Help & Manual</span>}
        </NavLink>

        {hasRole(user, "system") && (
          <SystemNavSection collapsed={collapsed} />
        )}
      </nav>
    </aside>
  );
}
